/**
 * Trainer dashboard UI kit — written from scratch.
 *
 * No react-bootstrap, no vendor template classes, no override layer.
 * Every piece renders plain elements against `dashboard.css`, so what
 * you read here is what the browser paints. Tones are passed as a name
 * ("sky", "good", "bad", …) and resolved to colour by one CSS rule.
 */

import { forwardRef } from 'react';

/* ── Section head: orange rule, title, optional date stamp and action ── */
export const Section = ({ title, stamp, action, onAction }) => (
  <div className="td-section">
    <h2>{title}</h2>
    {stamp && <span className="td-stamp">{stamp}</span>}
    {action && (
      <button type="button" className="td-section-action" onClick={onAction}>
        {action}
      </button>
    )}
  </div>
);

/* ── Hero: the dark insight panel that opens the page ── */
export const Hero = ({ eyebrow, title, sub, chips = [] }) => (
  <header className="td-hero">
    <div className="td-hero-grid" aria-hidden="true" />
    <div className="td-hero-inner">
      <div className="td-hero-text">
        {eyebrow && <div className="td-eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {sub && <p className="td-hero-sub">{sub}</p>}
        {chips.length > 0 && (
          <div className="td-hero-chips">
            {chips.map((c) => (
              <span className="td-hero-chip" key={c.key ?? c.label}>
                {c.icon}
                <b>{c.value}</b> {c.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  </header>
);

/**
 * KPI tile. `meter` is 0–100 and drives a 12-cell sparkbar, so the bar
 * always states a real proportion rather than decorating the number.
 */
export const Vital = ({ icon, value, label, tone = 'sky', meter = 0, chips = [], hint }) => {
  const filled = Math.max(0, Math.min(12, Math.round((meter / 100) * 12)));
  return (
    <div className={`td-vital td-tone-${tone}`}>
      <span className="td-vital-icon">{icon}</span>
      <div className="td-vital-value">{value}</div>
      <div className="td-vital-label">{label}</div>

      {chips.length > 0 ? (
        <div className="td-vital-chips">
          {chips.map((c) => (
            <span
              key={c.text}
              className={c.tone ? `td-badge td-tone-${c.tone}` : 'td-chip'}
            >
              {c.text}
            </span>
          ))}
        </div>
      ) : (
        hint && <p className="td-vital-hint">{hint}</p>
      )}

      <div className="td-spark" role="img" aria-label={`${Math.round(meter)} percent`}>
        {Array.from({ length: 12 }, (_, i) => (
          <i key={i} className={i < filled ? 'on' : undefined} />
        ))}
      </div>
    </div>
  );
};

export const VitalGrid = ({ children }) => <div className="td-vitals">{children}</div>;

/* ── Card, and the pieces that go inside it ── */
export const Card = ({ title, icon, count, countTone = 'good', sub, action, onAction, children }) => (
  <section className="td-card">
    <div className="td-card-head">
      <h3>
        {icon}
        {title}
        {count !== undefined && (
          <span className={`td-badge td-tone-${countTone}`}>{count}</span>
        )}
      </h3>
      {action && (
        <button type="button" className="td-section-action" onClick={onAction}>
          {action}
        </button>
      )}
      {sub && <p className="td-card-sub">{sub}</p>}
    </div>
    <div className="td-card-body">{children}</div>
  </section>
);

export const Row = forwardRef(({ onClick, children, ...rest }, ref) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      ref={ref}
      type={onClick ? 'button' : undefined}
      className={`td-row${onClick ? ' td-row-link' : ''}`}
      onClick={onClick}
      {...rest}
    >
      {children}
    </Tag>
  );
});
Row.displayName = 'Row';

export const Badge = ({ tone = 'sky', title, children }) => (
  <span className={`td-badge td-tone-${tone}`} title={title}>
    {children}
  </span>
);

export const Chip = ({ children, title }) => (
  <span className="td-chip" title={title}>{children}</span>
);

/**
 * A value against a yardstick. `muted` draws the reference bar (elapsed
 * time, target) so the gap between two meters reads as the shortfall.
 */
export const Meter = ({ label, value, tone = 'sky', muted = false }) => (
  <div className={`td-meter td-tone-${tone}`}>
    {label && <span className="td-meter-label">{label}</span>}
    <span className="td-meter-track">
      <span
        className={`td-meter-fill${muted ? ' muted' : ''}`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </span>
    <span className="td-meter-value">{value}%</span>
  </div>
);

export const More = ({ children, onClick, expanded }) => (
  <button type="button" className="td-more" onClick={onClick} aria-expanded={expanded}>
    {children}
  </button>
);

export const Empty = ({ icon, title, children }) => (
  <div className="td-empty">
    {icon}
    {title && <h4>{title}</h4>}
    <p>{children}</p>
  </div>
);

export const Loading = ({ children = 'Loading…' }) => (
  <div className="td-loading">
    <div className="td-spinner" />
    <p>{children}</p>
  </div>
);

/* ── Form controls ── */
export const Field = ({ label, children }) => (
  <div className="td-field">
    <label className="td-label">{label}</label>
    {children}
  </div>
);

export const Select = ({ value, onChange, options, placeholder, ...rest }) => (
  <select className="td-select" value={value} onChange={onChange} {...rest}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) =>
      typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
    )}
  </select>
);

export const Input = (props) => <input className="td-input" {...props} />;

export const Range = ({ children }) => <div className="td-range">{children}</div>;
export const RangeSep = () => <span className="td-range-sep">to</span>;

export const Button = ({ variant = '', children, ...rest }) => (
  <button type="button" className={`td-btn${variant ? ` td-btn-${variant}` : ''}`} {...rest}>
    {children}
  </button>
);

/* ── Tabs ── */
export const Tabs = ({ tabs, active, onChange }) => (
  <nav className="td-tabs" role="tablist">
    {tabs.map((t) => (
      <button
        key={t.key}
        type="button"
        role="tab"
        aria-selected={active === t.key}
        className={`td-tab${active === t.key ? ' on' : ''}`}
        onClick={() => onChange(t.key)}
      >
        {t.icon}
        <span>{t.label}</span>
      </button>
    ))}
  </nav>
);

/* ── Pager ── */
export const Pager = ({ page, totalPages, total, limit, onPage }) => (
  <div className="td-pager">
    <span className="td-muted">
      Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
    </span>
    <div className="td-pager-controls">
      <Button disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</Button>
      <span className="td-muted">Page {page} of {totalPages || 1}</span>
      <Button disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next</Button>
    </div>
  </div>
);
